import { create } from "zustand"

/**
 * Per-browser "preview as customer admin" switch for the admin dashboard's
 * Management tab, modeled on `tender-admin-mode-store.ts`.
 *
 * Unlike tender admin mode, this is view-only: no backend endpoint enforces
 * `require_admin_capability` yet, so there is nothing for a header to change
 * server-side. Turning this on only affects what the *current* platform
 * admin sees rendered — it fetches the live delegation matrix
 * (`GET /api/admin/delegation`) and filters the Management tab exactly as a
 * real customer admin would see it once endpoint enforcement ships. The
 * choice is remembered in localStorage and defaults to OFF.
 */

const STORAGE_KEY = "admin-customer-preview-mode"

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "on"
  } catch {
    return false
  }
}

function writeStored(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "on" : "off")
  } catch {
    // ignore (e.g. storage disabled / private mode)
  }
}

type AdminCustomerPreviewStore = {
  enabled: boolean
  setEnabled: (enabled: boolean) => void
  toggle: () => void
}

export const useAdminCustomerPreviewStore = create<AdminCustomerPreviewStore>()((set, get) => ({
  enabled: readStored(),
  setEnabled(enabled) {
    writeStored(enabled)
    set({ enabled })
  },
  toggle() {
    get().setEnabled(!get().enabled)
  },
}))
